import qs from 'qs';
import { type App } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { authMiddleware } from '@/core/plugins/router/auth-middleware';
import htmlLinkBehaviour from '@/core/plugins/router/html-link-behaviour';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { PermissionTarget } from '@/graphql/generated';
import ObjectAnalytics from '@/parts/analytics/pages/index.vue';
import JoinPage from '@/parts/auth/pages/Join.vue';
import LoginPage from '@/parts/auth/pages/Login.vue';
import SignInMaterPage from '@/parts/auth/pages/Master.vue';
import SignInProfilePage from '@/parts/auth/pages/SignInProfile.vue';
import SignInPage from '@/parts/auth/pages/SingInMaster.vue';
import Book from '@/parts/books/pages/_id.vue';
import Books from '@/parts/books/pages/index.vue';
import ObjectContent from '@/parts/content/pages/index.vue';
import DocumentsPage from '@/parts/documents/pages/index.vue';
import Error404 from '@/parts/errors/pages/404.vue';
import AccessDenied from '@/parts/errors/pages/AccessDenied.vue';
import Empty from '@/parts/errors/pages/Empty.vue';
import RoleNotAssigned from '@/parts/errors/pages/RoleNotAssigned.vue';
import LegalCreate from '@/parts/legals/pages/Create.vue';
import Legal from '@/parts/legals/pages/_id.vue';
import Legals from '@/parts/legals/pages/index.vue';
import ObjectCreate from '@/parts/objects/pages/ObjectCreate.vue';
import ObjectSettings from '@/parts/objects/pages/ObjectSettings.vue';
import Object from '@/parts/objects/pages/_id.vue';
import ObjectList from '@/parts/objects/pages/index.vue';
import Reconciliation from '@/parts/reconciliation/pages/index.vue';
import RoomCategories from '@/parts/room-categories/pages/index.vue';
import Support from '@/parts/support/pages/index.vue';
import TariffInfo from '@/parts/tariffs/pages/_id/Info.vue';
import TariffPlacingVariantsMaster from '@/parts/tariffs/pages/_id/PlacingVariantsMaster.vue';
import TariffPricesAndRestrictions from '@/parts/tariffs/pages/_id/PricesAndRestrictions.vue';
import TariffQuota from '@/parts/tariffs/pages/_id/Quota.vue';
import TariffServiceGroups from '@/parts/tariffs/pages/_id/ServiceGroups.vue';
import TariffSettings from '@/parts/tariffs/pages/_id/Settings.vue';
import Tariff from '@/parts/tariffs/pages/_id/index.vue';
import TariffCreate from '@/parts/tariffs/pages/controlled/Create.vue';
import TariffsControlled from '@/parts/tariffs/pages/controlled/index.vue';
import ChannelManagerIntegration from '@/parts/tariffs/pages/remote/Integration.vue';
import ChannelManagerList from '@/parts/tariffs/pages/remote/index.vue';
import ProfilePage from '@/parts/users/pages/CurrentUserProfile.vue';
import User from '@/parts/users/pages/_id.vue';
import Users from '@/parts/users/pages/index.vue';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),

  stringifyQuery: (query) => qs.stringify(query),

  parseQuery: (query) =>
    // in some cases default vue parser transforms '+' to ' '
    // so we should fix thi behaviour
    JSON.parse(JSON.stringify(qs.parse(query)).replace(/ /g, '+')),

  routes: [
    {
      path: '/',
      name: RouteNames.Home,
      component: ObjectList,
      meta: {
        layout: 'Default',
        permissions: [PermissionTarget.AccommodationObjects],
      },
    },

    {
      path: '/object/create',
      name: RouteNames.ObjectCreate,
      component: ObjectCreate,
      meta: {
        layout: 'Default',
        permissions: [PermissionTarget.AccommodationObjects],
      },
    },

    {
      path: '/object/:id/:acc_id',
      name: RouteNames.ObjectSettings,
      component: ObjectSettings,
      meta: {
        layout: 'Account',
        permissions: [PermissionTarget.AccommodationObjects],
      },
    },
    {
      path: '/object/:id/:acc_id/info',
      name: RouteNames.ObjectInfo,
      component: Object,
      meta: {
        layout: 'Account',
        permissions: [PermissionTarget.AccommodationObjects],
      },
    },

    {
      path: '/object/:id/:acc_id/create-tariff',
      component: TariffCreate,
      name: RouteNames.TariffCreate,
      meta: {
        layout: 'Account',
        permissions: [PermissionTarget.TariffSchedules],
      },
    },

    {
      path: '/object/:id/:acc_id/tariffs-controlled',
      component: TariffsControlled,
      name: RouteNames.TariffsControlled,
      meta: {
        layout: 'Account',
        permissions: [PermissionTarget.TariffSchedules],
      },
    },

    {
      path: '/object/:id/:acc_id/tariffs/:t_id',
      component: Tariff,
      name: RouteNames.Tariff,
      meta: {
        layout: 'Account',
      },
      children: [
        {
          path: '',
          component: TariffInfo,
          name: RouteNames.TariffInfo,
          meta: {
            permissions: [PermissionTarget.TariffSchedules],
          },
        },
        {
          path: 'settings',
          component: TariffSettings,
          name: RouteNames.TariffSettings,
          meta: {
            permissions: [PermissionTarget.TariffSchedules],
          },
        },
        {
          path: 'prices-and-restrictions',
          component: TariffPricesAndRestrictions,
          name: RouteNames.TariffPricesAndRestrictions,
          meta: {
            permissions: [PermissionTarget.TariffSchedules],
          },
        },
        {
          path: 'service-groups',
          component: TariffServiceGroups,
          name: RouteNames.TariffServiceGroups,
          meta: {
            permissions: [PermissionTarget.TariffSchedules],
          },
        },
        {
          path: 'quota',
          component: TariffQuota,
          name: RouteNames.TariffQuota,
          meta: {
            permissions: [PermissionTarget.TariffSchedules],
          },
        },
      ],
    },

    {
      path: '/object/:id/:acc_id/tariffs/:t_id/pvm',
      component: TariffPlacingVariantsMaster,
      name: RouteNames.TariffPlacingVariantsMaster,
      meta: {
        layout: 'Empty',
        permissions: [PermissionTarget.TariffSchedules],
      },
    },

    {
      path: '/object/:id/:acc_id/books',
      meta: {
        title: 'Бронирования',
        layout: 'Account',
      },
      children: [
        {
          path: '',
          name: RouteNames.Books,
          component: Books,
          meta: {
            permissions: [PermissionTarget.Orders],
          },
        },
        {
          path: ':b_id',
          component: Book,
          name: RouteNames.Book,
          meta: {
            permissions: [PermissionTarget.Orders],
          },
        },
      ],
    },

    {
      path: '/object/:id/:acc_id/reconciliation',
      component: Reconciliation,
      name: RouteNames.Reconciliation,
      meta: {
        layout: 'Account',
        permissions: [PermissionTarget.OrderReconciliation],
      },
    },

    {
      path: '/object/:id/:acc_id/room-categories',
      component: RoomCategories,
      name: RouteNames.RoomCategories,
      meta: {
        layout: 'Account',
        permissions: [PermissionTarget.RoomCategories],
      },
    },

    {
      path: '/object/:id/:acc_id/content',
      component: ObjectContent,
      name: RouteNames.ObjectContent,
      meta: {
        layout: 'Empty',
        permissions: [PermissionTarget.AccommodationObjectsContent],
      },
    },

    // Channel manager
    {
      path: '/object/:id/:acc_id/channel-manager',
      component: ChannelManagerList,
      name: RouteNames.CMList,
      meta: {
        layout: 'Account',
        permissions: [PermissionTarget.TariffSchedules],
      },
    },
    {
      path: '/object/:id/:acc_id/channel-manager/create',
      component: ChannelManagerIntegration,
      name: RouteNames.CMCreate,
      meta: {
        layout: 'Empty',
        permissions: [PermissionTarget.TariffSchedules],
      },
    },
    {
      path: '/object/:id/:acc_id/channel-manager/edit',
      component: ChannelManagerIntegration,
      name: RouteNames.CMEdit,
      props: {
        isEdit: true,
      },
      meta: {
        layout: 'Empty',
        permissions: [PermissionTarget.TariffSchedules],
      },
    },
    {
      path: '/object/:id/:acc_id/analytics',
      component: ObjectAnalytics,
      name: RouteNames.ObjectAnalytics,
      meta: {
        layout: 'Empty',
        permissions: [PermissionTarget.AccommodationObjects],
      },
    },

    // Support
    {
      path: '/support',
      component: Support,
      name: RouteNames.Support,
    },

    // ########################

    {
      path: '/login',
      name: RouteNames.Login,
      component: LoginPage,
      meta: {
        layout: 'Empty',
      },
    },
    {
      path: '/signin',
      component: SignInPage,
      name: RouteNames.SignIn,
      meta: {
        layout: 'Auth',
      },
    },
    {
      path: '/signin/master',
      component: SignInMaterPage,
      name: RouteNames.SignInMaster,
      meta: {
        layout: 'Empty',
      },
    },
    {
      path: '/signin/profile',
      component: SignInProfilePage,
      name: RouteNames.SignInProfile,
      meta: {
        layout: 'Auth',
      },
    },
    {
      path: '/documents',
      component: DocumentsPage,
      name: RouteNames.Documents,
      meta: {
        permissions: [PermissionTarget.Documents],
      },
    },
    {
      path: '/legal',
      component: Legals,
      name: RouteNames.Legals,
      meta: {
        permissions: [PermissionTarget.Legals],
      },
    },
    {
      path: '/legal/create',
      component: LegalCreate,
      name: RouteNames.LegalCreate,
      meta: {
        permissions: [PermissionTarget.Legals],
      },
    },
    {
      path: '/legal/:id',
      component: Legal,
      name: RouteNames.LegalDetail,
      meta: {
        permissions: [PermissionTarget.Legals],
      },
    },

    // Users pages
    {
      path: '/users',
      component: Users,
      name: RouteNames.Users,
      meta: {
        permissions: [PermissionTarget.Users],
      },
    },
    {
      path: '/users/:id',
      component: User,
      name: RouteNames.User,
      meta: {
        permissions: [PermissionTarget.Users],
      },
    },
    {
      path: '/profile',
      component: ProfilePage,
      name: RouteNames.Profile,
    },
    {
      path: '/join/:code',
      alias: '/join',
      component: JoinPage,
      name: RouteNames.Join,
      meta: {
        layout: 'Auth',
      },
    },

    {
      path: '/error/access-denied',
      component: AccessDenied,
      name: RouteNames.AccessDenied,
    },
    {
      path: '/error/role-not-assigned',
      component: RoleNotAssigned,
      name: RouteNames.RoleNotAssigned,
    },
    {
      path: '/error',
      component: Empty,
      name: RouteNames.Empty,
      meta: {
        layout: 'Empty',
      },
    },

    {
      path: '/:pathMatch(.*)*',
      component: Error404,
      name: RouteNames.Error404,
    },
  ],
});

export function routerPlugin(app: App) {
  router.beforeEach(async (to, from, next) => {
    await authMiddleware(to, from, next, app);
  });

  app.use(router);

  window.addEventListener('click', (e) => htmlLinkBehaviour(e, router));
}
